import type { ReactNode } from 'react';
import type { Toast as ToastInterface } from 'react-hot-toast';

import toast, { Toaster } from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import {
  CheckCircleIcon,
  CloseIcon,
  SpinnerGridIcon,
  WarningIcon,
} from '../../images/shapes';

interface ToastProps {
  /**
   * Component test id
   */
  testId?: string;
}

interface ExtendedToast extends ToastInterface {
  subMessage?: string;
}

const Toast = ({ testId }: ToastProps) => (
  <Toaster
    position="top-center"
    containerStyle={{
      top: 15,
    }}
    toastOptions={{
      duration: 3000,
      success: {
        icon: (
          <CheckCircleIcon className="w-6 text-lime-300" aria-hidden="true" />
        ),
        className: 'border-lime-300',
      },
      error: {
        icon: <WarningIcon className="w-6 text-red" aria-hidden="true" />,
        className: 'border-red',
      },
      loading: {
        icon: <SpinnerGridIcon className="w-6 h-6 animate-spin" />,
        className: 'border-slate-100',
      },
    }}
  >
    {(t: ExtendedToast) => (
      <div
        className={twMerge(
          `z-50 max-w-sm w-full bg-white dark:bg-slate-900 shadow-xl rounded-lg`,
          `pointer-events-auto border ${t.className} overflow-hidden`,
        )}
        style={t.style}
      >
        <div className="p-3">
          <div className="flex items-center">
            <div className="shrink-0">{t.icon}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-base font-semibold text-black dark:text-white">
                {t.message as ReactNode}
              </p>
              <p className="mt-1 text-sm text-black dark:text-white">
                {t.subMessage}
              </p>
            </div>
            <div className="ml-4 shrink-0 flex self-start">
              <button
                type="button"
                className="inline-flex text-black focus:outline-hidden"
                onClick={() => toast.remove(t.id)}
                {...getTestProps(testId, 'close')}
              >
                <span className="sr-only">Close</span>
                <CloseIcon className="w-3 dark:text-white" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </Toaster>
);

export default Toast;
